import {createAsyncThunk, createSlice} from '@reduxjs/toolkit';
import ProviderManager from 'app/provider/providerManager';
import {getRecaptchaToken} from 'app/utilities/recaptcha/recaptcha';
import {showAppLoader, hideAppLoader} from 'app/Components/Shared/Loader/LoaderSlice';
export const submitContactForm = createAsyncThunk(
  'leadContactForm/submitForm',
  async (data, thunkAPI) => {
    try {
      thunkAPI.dispatch(showAppLoader());

      const blockState = thunkAPI.getState().leadContactForm;
      const isAgreementsCheckBoxActive = blockState.isAgreementsCheckBoxActive;

      const blockId = blockState?.contentData?.blockId;
      const {
        firstName,
        lastName,
        identity,
        payment,
        phoneNumber,
        additionalPhone,
        emailAddress,
        companyName,
        content,
        generalField,
        terms,
        subjectIndex,
      } = blockState.formData;

      const uploadFiles = blockState?.uploadedFiles?.length > 0
        ? blockState.uploadedFiles.map((x) => x.storageName || x.name)
        : [];

      const subjectObj = subjectIndex ? blockState.contentData.subjectList[subjectIndex - 1] : null;
      const subject = subjectObj?.subjectCode;

      const recaptchaToken = await getRecaptchaToken('leadContactForm');
      const result = await ProviderManager.getProviderData('sendExtendedLead', [
        recaptchaToken,
        blockId,
        firstName,
        lastName,
        identity,
        payment,
        phoneNumber,
        additionalPhone,
        emailAddress,
        companyName,
        content,
        generalField,
        terms,
        subject,
        uploadFiles,
        isAgreementsCheckBoxActive,
        data?.utmParams
      ]);

      return {
        isSuccess: result?.Body?.isSuccess,
        ticketNumber: result?.Body?.ticketNumber,
        sla: result?.Body?.sla,
        collaborationUpdateSuccess: result?.Body?.collaborationUpdateSuccess,
        returnCodeMessage: result?.Body?.returnCodeMessage,
      };
    } catch (error) {
      return {
        isSuccess: false,
      };
    } finally {
      thunkAPI.dispatch(hideAppLoader());
    }
  }
);

const initialState = {
  contentData: {
    blockId: null,
    subjectList: [],
  },
  formData: {
    firstName: '',
    lastName: '',
    identity: '',
    payment: '',
    phoneNumber: '',
    additionalPhone: '',
    emailAddress: '',
    companyName: '',
    content: '',
    generalField: '',
    terms: '',
    subjectIndex: '',
  },
  uploadedFiles: [],
  fileUploadError: null,
  errors: {},
  isSubmitting: false,
  submitStatus: null, // null | 'success' | 'failure'
  submitResponse: null,
  isAgreementsCheckBoxActive: false,
};

const leadContactFormSlice = createSlice({
  name: 'leadContactForm',
  initialState,

  reducers: {
    setInitialContent: (state, action) => {
      state.contentData = {
        blockId: action.payload.contentLink?.id,
        subjectList: action.payload.subjectList?.expandedValue?.map((x) => ({
          subjectName: x.subjectName?.value,
          subjectCode: x.subjectCode?.value,
          blockId: x.contentLink?.id,
        })) || [],
      };
    },

    setFieldValue: (state, action) => {
      const {fieldName, value} = action.payload;
      state.formData[fieldName] = value;
      // Clear error when user starts typing
      if (state.errors[fieldName]) {
        delete state.errors[fieldName];
      }
    },

    setFieldError: (state, action) => {
      const {fieldName, error} = action.payload;
      state.errors[fieldName] = error;
    },

    clearErrors: (state) => {
      state.errors = {};
    },

    addFiles: (state, action) => {
      const newFiles = action.payload;
      state.uploadedFiles = [...state.uploadedFiles, ...newFiles];
      state.fileUploadError = null;
    },

    removeFile: (state, action) => {
      const index = action.payload;
      state.uploadedFiles = state.uploadedFiles.filter((_, i) => i !== index);
      state.fileUploadError = null;
    },

    setFileUploadError: (state, action) => {
      state.fileUploadError = action.payload;
    },

    clearFiles: (state) => {
      state.uploadedFiles = [];
      state.fileUploadError = null;
    },

    resetForm: (state) => {
      state.formData = initialState.formData;
      state.uploadedFiles = [];
      state.fileUploadError = null;
      state.errors = {};
      state.submitStatus = null;
      state.submitResponse = null;
    },

    resetBlock: () => initialState,

    setAgreementsCheckBox: (state, action) => {
      state.isAgreementsCheckBoxActive = action.payload;
    },
  },

  extraReducers: (builder) => {
    builder
      .addCase(submitContactForm.pending, (state) => {
        state.isSubmitting = true;
        state.submitStatus = null;
      })
      .addCase(submitContactForm.fulfilled, (state, action) => {
        state.isSubmitting = false;
        state.submitResponse = action.payload;
        if (action.payload?.isSuccess) {
          state.submitStatus = 'success';
          state.formData = initialState.formData; // Reset form on success
        } else {
          state.submitStatus = 'failure';
        }
      })
      .addCase(submitContactForm.rejected, (state, action) => {
        state.isSubmitting = false;
        state.submitStatus = 'failure';
        state.submitResponse = {error: action.payload};
      });
  },
});

export const {
  setInitialContent,
  setFieldValue,
  setFieldError,
  clearErrors,
  addFiles,
  removeFile,
  setFileUploadError,
  clearFiles,
  resetForm,
  resetBlock,
  setAgreementsCheckBox,
} = leadContactFormSlice.actions;

export default leadContactFormSlice.reducer;

